import { useQuery } from "@tanstack/react-query";
import { api } from "@/src/core/api/client";
import type {
  IHomepageRail,
  IHomepageRailsResponse,
  IHomepageTabsData,
  IHomepageTabsResponse,
} from "../types";

// Python service (`api.python`).
const HOMEPAGE_BASE = "/public/homepage";

const STALE_TIME = 5 * 60 * 1000;
const GC_TIME = 30 * 60 * 1000;

const homepageKeys = {
  all: ["homepage"] as const,
  tabs: () => [...homepageKeys.all, "tabs"] as const,
  rails: (slug: string) => [...homepageKeys.all, "rails", slug] as const,
};

/** Raw calls, also used by the server pages. Throw an ApiError on failure. An object so tests can spy on it. */
export const homepageApi = {
  /**
   * A tab's Lottie animation from the CDN; null when it is missing or not JSON. Plain
   * `fetch`, not `api.external`: auth headers would turn this into a CORS preflight.
   */
  async getAnimation(url: string): Promise<object | null> {
    const response = await fetch(url);
    // A missing object comes back as a 200-shaped XML error body from the CDN.
    if (!response.ok) return null;
    const data: unknown = await response.json().catch(() => null);
    return data && typeof data === "object" ? data : null;
  },

  /** Active tabs; with a token, `default_tab` is the user's last-searched tab. */
  async getTabs(): Promise<IHomepageTabsResponse> {
    const { data } = await api.python.get<IHomepageTabsResponse>(
      `${HOMEPAGE_BASE}/tabs`,
    );
    return data;
  },
  /** One tab's rails and their cards, pins first. */
  async getTabRails(slug: string): Promise<IHomepageRailsResponse> {
    const { data } = await api.python.get<IHomepageRailsResponse>(
      `${HOMEPAGE_BASE}/tabs/${encodeURIComponent(slug)}/rails`,
    );
    return data;
  },
};

/** Tabs + default tab. Server data, when given, only fills the first paint (refetched on mount). */
export const useHomepageTabsQuery = (initialData?: IHomepageTabsData) =>
  useQuery({
    queryKey: homepageKeys.tabs(),
    queryFn: async (): Promise<IHomepageTabsData> => {
      const response = await homepageApi.getTabs();
      return { tabs: response.data, defaultTab: response.default_tab };
    },
    initialData,
    initialDataUpdatedAt: initialData ? 0 : undefined,
    staleTime: STALE_TIME,
  });

/** One tab's rails; disabled until a slug is known. */
export const useTabRailsQuery = (
  slug: string | null,
  initialRails?: IHomepageRail[],
) =>
  useQuery({
    queryKey: homepageKeys.rails(slug ?? "none"),
    queryFn: async () => (await homepageApi.getTabRails(slug as string)).data,
    enabled: slug !== null,
    initialData: initialRails,
    initialDataUpdatedAt: initialRails ? 0 : undefined,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
    refetchOnReconnect: false,
  });
